import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import produce from 'immer';
import { merge } from 'lodash';

import { Button } from '~/components';
import FormDrawer from '~/components/FormDrawer';
import { PlusIcon } from '~/img';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import buildNamePath from '~/utils/buildNamePath';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { useGetApplication } from '~/controllers/wizard';
import { hasAtLeastOneDeclinedField } from '~/utils/softDeclinesFields';
import { isEditButtonDisabled} from '~/components/views/ShareholdersMultiple/utils';
import { KsaShareholders } from '~/types/ksa/KsaShareholders';
import BeneficialOwnerForm, {
  formName,
  getBeneficialOwnersFormItemsNamePath,
} from '~/pages/ksa/application/shareholders/components/beneficialOwners/BeneficialOwnerForm';
import { stepName } from '~/pages/ksa/application/shareholders';

const BeneficialOwnerAdd: FC<Partial<FormItemInjectedProps<KsaShareholders>>> = (props) => {
  const { value, onChange } = props as FormItemInjectedProps<KsaShareholders>;
  const shareholdersFormState = { shareholders: value };

  const { t } = useTranslation('ksaApplication');
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const newShareholderIndex = shareholdersFormState.shareholders?.beneficialOwners?.length || 0;

  const onFinish = (newValues: ApplicationPayload) => {
    const updatedData = produce(shareholdersFormState, (draft) => {
      merge(draft, newValues);
    });
    onChange?.(updatedData.shareholders);
    handleClose();
  };

  const applicationQuery = useGetApplication();
  const hasBeneficialOwnersValidationWarnings = hasAtLeastOneDeclinedField(
    applicationQuery.data,
    stepName
  );

  return (
    <>
      <Button
        id="addNewBeneficialOwnerButton"
        extraLarge
        icon={<PlusIcon />}
        disabled={isEditButtonDisabled(
          applicationQuery.data,
          hasBeneficialOwnersValidationWarnings
        )}
        onClick={handleOpen}
      >
        {t('Add beneficial owner')}
      </Button>
      <FormDrawer
        id="beneficialOwnerAddDrawer"
        visible={open}
        onClose={handleClose}
        title={t('Add beneficial owner')}
        formName={formName}
      >
        <BeneficialOwnerForm
          onFinish={onFinish}
          initialValues={shareholdersFormState}
          n={buildNamePath(getBeneficialOwnersFormItemsNamePath(newShareholderIndex))}
        />
      </FormDrawer>
    </>
  );
};

export default BeneficialOwnerAdd;
